import type { ReactNode } from 'react';
import { Fragment } from 'react';
import type Maybe from 'true-myth/maybe';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useLearningOpportunitiesPermissions } from '~/shared/query-hooks/learning-opportunities';

import { useAnnouncementsMegaphoneListItem } from '../../../announcements/use-announcements-megaphone-list-item';
import {
    useJobArchitectureListItem,
    useSkillsListItem,
} from '../../../job-architecture-v2/use-job-architecture-navigation-list-items';
import { useDashboardV2ListItem } from '../../pages/dashboard-v2/use-dashboard-v2-list-item';
import { useFeaturedLearningOpportunityOptions } from '../../query-hooks/featured-learning-opportunities';
import { useJobProfilesPermissions } from '../../query-hooks/job-profiles/job-profiles';
import { useJobsPermissions } from '../../query-hooks/jobs';
import { useMyProfilePermissionsQuery } from '../../query-hooks/profile';
import { useTalentDirectoryPermissions } from '../../query-hooks/talent-directory/talent-directory';
import { useBusinessIntelligencePermissions } from '../../query-hooks/use-business-intelligence-options';
import { useUserBookmarksPermission } from '../../query-hooks/users/bookmark';
import type {
    SideDrawerLinkProperties,
    SideDrawerListItem,
    SideDrawerParentLinkProperties,
} from '../navigation-menu-list-item';
import {
    createAlignmentRecordItem,
    createAnalyticsDashboardListItem,
    createControlRoomListItem,
    createDashboardListItem,
    createDocumentationListItem,
    createJobProfileManagementListItem,
    createLearningManagementListItem,
    createMyGrowthListItem,
    createPositionManagementItem,
    createSettingsListItem,
    createTalentDirectoryListItem,
} from '../navigation-menu-list-item';
import { SideDrawerLinkV2 } from './SideDrawerLinkV2';

const isSideDrawerParentLink = (link: SideDrawerListItem): link is SideDrawerParentLinkProperties =>
    'children' in link && Array.isArray(link.children);

const renderSideDrawerListItem = (link: SideDrawerListItem): ReactNode => {
    if (isSideDrawerParentLink(link)) {
        return <Fragment key={link.label}>{link.children.map((child) => renderSideDrawerListItem(child))}</Fragment>;
    }

    const leafLink: SideDrawerLinkProperties = link;

    return (
        <SideDrawerLinkV2
            key={leafLink.label}
            externalUrl={leafLink.externalUrl}
            Icon={leafLink.Icon}
            label={leafLink.label}
            NewIcon={leafLink.NewIcon}
            onRouteChange={leafLink.onRouteChange}
            path={leafLink.path}
        />
    );
};

type SideDrawerLinksResult = {
    readonly bottomItem: ReactNode;
    readonly links: readonly ReactNode[];
};

export const useSideDrawerLinks = (): SideDrawerLinksResult => {
    const {
        applicationState: { customization, featureFlagService, isAssumedTenant, myUserProfile },
        translate,
    } = useApplicationContext();

    const { canUserManageJobs } = useJobsPermissions();
    const { canAccessTalentDirectory } = useTalentDirectoryPermissions();
    const { canUserFilterLearningOpportunities } = useLearningOpportunitiesPermissions();
    const { canUserGetJobProfiles } = useJobProfilesPermissions();
    const { canAccessBusinessIntelligence } = useBusinessIntelligencePermissions();
    const { data: myProfilePermissions } = useMyProfilePermissionsQuery();

    const canUseControlRoom = myProfilePermissions?.canUseControlRoom ?? false;
    const tenantId = customization.get('tenantId');
    const myUserProfileId = myUserProfile.get('id').unwrapOr('');

    const { data: featuredOptions } = useFeaturedLearningOpportunityOptions(myUserProfileId);
    const { data: permissions } = useUserBookmarksPermission(myUserProfileId);

    const showFeaturedLearningOpportunities = featuredOptions?.canReadUserFeaturedOpportunities ?? false;
    const showBookmarks = permissions?.canReadBookmarks === true;
    const showGrowthPage = showFeaturedLearningOpportunities && showBookmarks;

    const announcementsMegaphone = useAnnouncementsMegaphoneListItem();
    const dashboardV2 = useDashboardV2ListItem();
    const jobArchitecture = useJobArchitectureListItem();
    const skills = useSkillsListItem();

    const renderMaybeItem = (item: Maybe<SideDrawerListItem>): ReactNode =>
        item.isJust ? renderSideDrawerListItem(item.value) : null;

    return {
        bottomItem: announcementsMegaphone,
        links: [
            dashboardV2,
            renderMaybeItem(createDashboardListItem({ featureFlagService, translate })),
            renderMaybeItem(createDocumentationListItem({ translate })),
            renderMaybeItem(createMyGrowthListItem({ featureFlagService, showGrowthPage, translate })),
            renderMaybeItem(createAnalyticsDashboardListItem({ canAccessBusinessIntelligence, translate })),
            renderMaybeItem(
                createJobProfileManagementListItem({
                    canUserGetJobProfiles,
                    canUserManageJobs,
                    translate,
                }),
            ),
            renderMaybeItem(createLearningManagementListItem({ canUserFilterLearningOpportunities, translate })),
            renderMaybeItem(createTalentDirectoryListItem({ canAccessTalentDirectory, translate })),
            jobArchitecture,
            skills,
            renderMaybeItem(createPositionManagementItem({ featureFlagService, translate })),
            renderMaybeItem(createAlignmentRecordItem({ featureFlagService, translate })),
            renderMaybeItem(
                createControlRoomListItem({
                    canUseControlRoom,
                    featureFlagService,
                    isAssumedTenant,
                    tenantId,
                    translate,
                }),
            ),
            renderMaybeItem(
                createSettingsListItem({
                    featureFlagService,
                    translate,
                    userRoles: myUserProfile.map((profile) => profile.roles ?? []).unwrapOr([]),
                }),
            ),
        ],
    };
};
